import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import type { ReduxRootState } from "types/redux-store";
import type { PostFormState } from "shared/types/post";
import { saveDraftsToStorage } from "./draft-storage";

// Auto-save interval: 30 seconds
const AUTO_SAVE_INTERVAL_MS = 30000;

// Helper function to filter forms that should be auto-saved
// Validates that form has required data to prevent serialization errors
function getFormsToSave(currentForms: PostFormState[]) {
  return currentForms.filter(
    (form) =>
      form.isDirty && // Only save dirty forms
      !form.formSaveData && // Exclude already-saved forms
      form.post && // Must have post object
      form.post._id && // Must have post ID for serialization
      !form.post.past // Exclude past posts
  );
}

/**
 * React hook that enables automatic draft saving for post forms
 *
 * Features:
 * - Periodic auto-save every 30 seconds
 * - Save on browser close/reload (beforeunload event)
 * - Only saves forms marked as dirty and not already saved
 * - Excludes past posts from auto-save
 * - Uses refs to avoid recreating intervals on form state changes
 * - Includes user ID validation to prevent cross-user contamination
 * - Pauses auto-save when tab is hidden to prevent timing issues
 *
 * Usage: Call this hook once in the BulkTabs component
 */
export function useAutoSaveDrafts() {
  const forms = useSelector((state: ReduxRootState) =>
    Object.values(state.forms.postForms.byId)
  );
  const userId = useSelector((state: ReduxRootState) => state.user?.userId);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef<boolean>(!document.hidden);

  // Use refs to access latest values without recreating effects
  const formsRef = useRef<PostFormState[]>(forms);
  const userIdRef = useRef<string | undefined>(userId);

  // Update refs when values change
  useEffect(() => {
    formsRef.current = forms;
  }, [forms]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Start the auto-save interval
  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Skip save if tab is hidden to prevent timing issues
      if (!isVisibleRef.current) {
        return;
      }

      const currentForms = formsRef.current;
      const currentUserId = userIdRef.current;
      const dirtyForms = getFormsToSave(currentForms);

      if (dirtyForms.length > 0) {
        saveDraftsToStorage(dirtyForms, currentUserId);
      }
    }, AUTO_SAVE_INTERVAL_MS);
  }, []);

  // Stop the auto-save interval
  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cancel any pending visibility timeout
  const cancelPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handle visibility change - pause auto-save when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;

      // Always cancel pending timeout to prevent race conditions
      cancelPendingTimeout();

      if (document.hidden) {
        // Tab is hidden - stop auto-save to prevent timing issues
        stopInterval();
      } else {
        // Tab is visible - restart auto-save
        // Small delay to let React reconcile state after tab becomes active
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          startInterval();
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      cancelPendingTimeout();
    };
  }, [startInterval, stopInterval, cancelPendingTimeout]);

  // Periodic auto-save effect (30 seconds)
  // Only created once, reads from refs to get latest forms and userId
  useEffect(() => {
    startInterval();

    return () => {
      stopInterval();
      cancelPendingTimeout();
    };
  }, [startInterval, stopInterval, cancelPendingTimeout]);

  // beforeunload save effect (browser close/reload)
  // Only registered once, reads from refs to get latest forms and userId
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      try {
        const currentForms = formsRef.current;
        const currentUserId = userIdRef.current;
        const dirtyForms = getFormsToSave(currentForms);

        if (dirtyForms.length > 0) {
          // Use synchronous save on beforeunload (must complete before page closes)
          saveDraftsToStorage(dirtyForms, currentUserId);
          // Trigger browser confirmation dialog to ensure save completes
          e.preventDefault();
          e.returnValue = "";
        }
      } catch (error) {
        // Can't show toast during beforeunload, but log for debugging
        console.error("Failed to save drafts on beforeunload:", error);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // Empty deps - only register listener once
}
